/**
 * Custom panel implementation for 8x8.
 * Contains an iframe that loads the custom panel URL with JWT and meetingId params.
 */
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isMobileBrowser } from '../../../base/environment/utils';
import { IconCloseLarge } from '../../../base/icons/svg';
import ClickableIcon from '../../../base/ui/components/web/ClickableIcon';
import { close } from '../../actions.web';
import { getCustomPanelConfiguredWidth, getCustomPanelOpen, getCustomPanelUrl } from '../../functions';
const useStyles = makeStyles()((theme, { isChatOpen, panelWidth }) => {
    return {
        customPanel: {
            backgroundColor: theme.palette.ui01,
            flexShrink: 0,
            position: 'relative',
            transition: 'width .16s ease-in-out',
            width: `${panelWidth}px`,
            zIndex: isMobileBrowser() && isChatOpen ? -1 : 0,
            display: 'flex',
            flexDirection: 'column',
            fontWeight: 600,
            height: '100%',
            [['& > *:first-child', '& > *:last-child']]: {
                flexShrink: 0
            },
            '@media (max-width: 580px)': {
                height: '100dvh',
                position: 'fixed',
                left: 0,
                right: 0,
                top: 0,
                width: '100%'
            }
        },
        header: {
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            height: '48px',
            padding: '12px',
            justifyContent: 'flex-end',
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 1,
            '& button': {
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                borderRadius: '6px',
                padding: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.95)'
                }
            }
        },
        iframeContainer: {
            boxSizing: 'border-box',
            flex: 1,
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            height: '100%'
        },
        iframe: {
            border: 'none',
            height: '100%',
            width: '100%'
        }
    };
});
/**
 * Component that renders the custom panel with an iframe.
 *
 * @returns {JSX.Element | null} The custom panel component or null if not open.
 */
const CustomPanel = () => {
    // TODO: Before releasing to prod the final version we might need to disable the panel for the breakout rooms
    // because the transcriptions won't work.
    const isChatOpen = useSelector((state) => state['features/chat'].isOpen);
    const panelWidth = getCustomPanelConfiguredWidth();
    const { classes } = useStyles({ isChatOpen,
        panelWidth });
    const paneOpen = useSelector(getCustomPanelOpen);
    const jwt = useSelector((state) => state['features/base/jwt'].jwt);
    const conference = useSelector((state) => state['features/base/conference'].conference);
    const meetingId = conference?.getMeetingUniqueId();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const onClosePane = useCallback(() => {
        dispatch(close());
    }, [dispatch]);
    /**
     * Constructs the iframe URL with JWT and meetingId query parameters.
     */
    const iframeUrl = useMemo(() => {
        const baseUrl = getCustomPanelUrl();
        if (!baseUrl) {
            return '';
        }
        const url = new URL(baseUrl);
        if (jwt) {
            url.searchParams.set('token', jwt);
        }
        if (meetingId) {
            url.searchParams.set('meeting', meetingId);
        }
        return url.toString();
    }, [jwt, meetingId]);
    if (!paneOpen) {
        return null;
    }
    return (React.createElement("div", { className: classes.customPanel, id: 'custom-panel' },
        React.createElement("div", { className: classes.header },
            React.createElement(ClickableIcon, { accessibilityLabel: t('customPanel.close', 'Close'), icon: IconCloseLarge, onClick: onClosePane })),
        React.createElement("div", { className: classes.iframeContainer }, iframeUrl && (React.createElement("iframe", { allow: 'camera; microphone; display-capture', className: classes.iframe, src: iframeUrl, title: 'Custom Panel' })))));
};
export default CustomPanel;
